/**
 * "Give feedback", the dialog.
 *
 * It is deliberately the smallest form in the product: two choices, one box,
 * one button. Everything else it records, it records by itself - who, when,
 * which screen, and the trail of screens just before - because a person in the
 * middle of being confused is the last person who should be asked to describe
 * their own click path.
 *
 * Three rules shape the copy. It is **business language** (J3: feedback, never
 * "fault logging"; people hesitate to file a fault about their own confusion,
 * which is exactly the entry worth having). It is **honest about what travels
 * with the words**, in one line rather than a policy paragraph, because a
 * quiet line a person can read in two seconds is what informed consent looks
 * like on a form this small. And it **does not close in silence**: a dialog
 * that vanishes with no word reads as a failure to anybody who has ever lost
 * text to a form.
 */

import { CircleCheck } from "lucide-react"
import * as React from "react"

import { Modal } from "@/components/modal"
import { Button, Field, FormError, Textarea } from "@/components/ui"
import { ChoiceRow } from "@/features/wizard/picker"
import { feedbackContext } from "@/lib/trail"

import { useSubmitFeedback } from "./api"
import { fieldErrorFrom, formErrorFrom } from "./errors"
import { CATEGORY_CHOICES, CATEGORY_PROMPTS, type FeedbackCategory } from "./model"

/** The sentence, in one place, because the dialog shows it and the chrome announces it. */
export const THANK_YOU = "Thank you. Your feedback has been recorded."

/**
 * How long the thanks stays up before the dialog closes itself.
 *
 * Long enough to be read at a glance and short enough that nobody reaches for
 * the Close button first. The button is there regardless, for whoever does.
 */
const DISMISS_MS = 2400

export function FeedbackDialog({
  route,
  onClose,
  onSent,
}: {
  /** The screen the button was pressed on, captured by the chrome at open time. */
  route: string
  onClose: () => void
  /** Fired once when the entry lands, so the chrome can announce it politely. */
  onSent: () => void
}) {
  const [category, setCategory] = React.useState<FeedbackCategory>("problem")
  const [description, setDescription] = React.useState("")
  const [fieldError, setFieldError] = React.useState<string | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [sent, setSent] = React.useState(false)
  const submit = useSubmitFeedback()

  // One group name per mounted dialog. A hardcoded `name` is how two radio
  // groups rendered from the same component collide into one, which this
  // product has already been bitten by once in `DataTable`'s dual render.
  const group = React.useId()

  const wordsRef = React.useRef<HTMLTextAreaElement>(null)
  const closeRef = React.useRef<HTMLButtonElement>(null)

  /**
   * The cursor starts in the box, which `autoFocus` cannot deliver here.
   *
   * Measured in Chromium, not reasoned about: React's `autoFocus` focuses the
   * element as it mounts, while the dialog is still closed and nothing inside
   * it is focusable, and `Modal` then calls `showModal()` in its own effect,
   * which moves focus to the dialog's first focusable control - the close
   * button in the band. So a person opening this by keyboard landed on Close,
   * where a typed space activates the button and throws the dialog away
   * mid-sentence. A child's effects run before its parent's, so `Modal` has
   * already shown the dialog by the time this runs, and this wins.
   */
  React.useEffect(() => {
    wordsRef.current?.focus()
  }, [])

  /**
   * And once it is sent, focus follows the form that has gone.
   *
   * Without this the Send button unmounts under the keyboard and focus falls
   * to the document body, which is where a keyboard user has nothing to press
   * and no idea what happened. Done is the control that replaces it.
   */
  React.useEffect(() => {
    if (sent) closeRef.current?.focus()
  }, [sent])

  React.useEffect(() => {
    if (!sent) return
    const timer = setTimeout(onClose, DISMISS_MS)
    return () => clearTimeout(timer)
  }, [sent, onClose])

  function send() {
    setFieldError(null)
    setFormError(null)
    submit.mutate(
      {
        category,
        description,
        route,
        // The trail attaches here and nowhere else: it is read at the moment
        // of sending and never stored, so nothing carries it around.
        context: feedbackContext(),
      },
      {
        onSuccess: () => {
          setSent(true)
          onSent()
        },
        onError: (cause: unknown) => {
          // The server decides which field it objected to; a blank description
          // comes back blaming `description`, so the message lands under the
          // box the person typed in rather than in a banner they must map.
          const onField = fieldErrorFrom(cause, "description")
          if (onField) setFieldError(onField)
          else {
            setFormError(
              formErrorFrom(cause, "Your feedback could not be sent. Please try again."),
            )
          }
        },
      },
    )
  }

  return (
    <Modal
      title="Give feedback"
      description={
        sent
          ? undefined
          : "This build is under review. Tell us what got in your way, or what would make it better."
      }
      size="md"
      onClose={onClose}
      footer={
        sent ? (
          // "Done" rather than "Close", because the band already carries a
          // control whose accessible name is Close: two buttons with one name
          // in one dialog is a sentence a screen reader reads twice and a
          // person has to disambiguate for no reason.
          <Button ref={closeRef} onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={submit.isPending}>
              Cancel
            </Button>
            <Button onClick={send} loading={submit.isPending}>
              Send feedback
            </Button>
          </>
        )
      }
    >
      {sent ? (
        <div className="flex items-start gap-3 py-2">
          <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" aria-hidden />
          <div>
            <p className="text-sm font-medium">{THANK_YOU}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              We read every entry while the system is under review.
            </p>
          </div>
        </div>
      ) : (
        <>
          {formError && <FormError className="mb-4">{formError}</FormError>}

          <fieldset className="mb-4">
            {/* A fieldset and legend rather than a label, because the thing
                being named is a group of controls and not one input. */}
            <legend className="mb-1.5 block text-[13px] font-medium text-foreground/90">
              What kind of feedback is this?
            </legend>
            {/* The wizard's own choice row: the whole row is the label, so the
                target is a row rather than a 16-pixel circle. Two options are
                shown rather than collapsed into a list somebody has to open:
                the second choice is what tells a person the first one is not
                their only way in. */}
            <ul className="divide-y overflow-hidden rounded-lg border">
              {CATEGORY_CHOICES.map((choice) => (
                <ChoiceRow
                  key={choice.value}
                  control="radio"
                  name={group}
                  selected={category === choice.value}
                  onSelect={() => setCategory(choice.value)}
                  title={choice.title}
                  detail={choice.detail}
                />
              ))}
            </ul>
          </fieldset>

          <Field label="In your own words" error={fieldError} required>
            <Textarea
              ref={wordsRef}
              value={description}
              onChange={(event) => {
                setDescription(event.target.value)
                if (fieldError) setFieldError(null)
              }}
              placeholder={CATEGORY_PROMPTS[category]}
              rows={5}
            />
          </Field>

          {/* The one line about the trail. Not a policy paragraph and not
              tracking language: it says what is attached and why, which is
              what a person needs to decide whether to press the button. */}
          <p className="text-xs text-muted-foreground">
            What you were doing just before is included, to help us understand the
            problem.
          </p>
        </>
      )}
    </Modal>
  )
}
